import React, { useMemo } from 'react';

const CARD_SLOTS = 4;

function randomCardCount() {
  return Math.floor(Math.random() * 4); // 0-3, inclusive
}

/**
 * Placeholder shown in place of OasisBoard while a game's data is still
 * loading -- deliberately reuses OasisBoard's own layout classes
 * (.oasis-board-header, .oasis-board-row, .oasis-board-hand-area, etc.) so
 * the page's shape doesn't jump once the real board mounts in its place;
 * only the content inside each piece is swapped for plain shimmering blocks
 * (.skeleton-block). Each column shows a random handful of placeholder
 * cards (not a fixed count) so the loading state doesn't look like a
 * suspiciously uniform, obviously-fake board.
 */
export default function OasisBoardSkeleton() {
  const columns = [0, 1, 2, 3, 4];

  // Randomized once per mount, not on every render -- a loading skeleton
  // that kept reshuffling itself would read as jittery rather than "loading".
  const cardCounts = useMemo(
    () => columns.map(() => ({ opponent: randomCardCount(), mine: randomCardCount() })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Mirrors OasisBoard's own renderColumnZone: my zone sits just below the
  // oasis row, so filled slots (closest to oasis) come first, empty ones
  // trail below. The opponent's zone sits above the oasis row, so closest-
  // to-oasis is the *bottom* -- empty slots come first there, with filled
  // ones trailing down toward the oasis, instead of every card magnetizing
  // to the top of the board.
  function renderSlots(count, side) {
    const filled = Array.from({ length: count }).map((_, i) => (
      <div key={`filled-${i}`} className="oasis-board-slot oasis-board-filled-slot">
        <div className="skeleton-block skeleton-card" />
      </div>
    ));
    const empty = Array.from({ length: CARD_SLOTS - count }).map((_, i) => (
      <div key={`empty-${i}`} className="oasis-board-slot" />
    ));
    return side === 'opponent' ? [...empty, ...filled] : [...filled, ...empty];
  }

  return (
    <div className="oasis-board" aria-hidden="true">
      <div className="oasis-board-header">
        <div className="oasis-board-opponent-info">
          <div className="oasis-board-opponent-name-row">
            <span className="skeleton-block skeleton-name-lg" />
            <span className="skeleton-block skeleton-palm-trees" />
          </div>
          <div className="oasis-board-hand-mini">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="skeleton-block skeleton-hand-mini-card" />
            ))}
          </div>
        </div>
        <div className="oasis-board-status">
          <span className="skeleton-block skeleton-round" />
          <span className="skeleton-block skeleton-score" />
        </div>
      </div>

      <div className="oasis-board-row">
        <div key="deck-spacer-opp-cards" />
        {columns.map(i => (
          <div key={`opp-${i}`} className="oasis-board-cards oasis-board-cards-opponent">
            {renderSlots(cardCounts[i].opponent, 'opponent')}
          </div>
        ))}

        <div key="deck-spacer-opp-points" />
        {columns.map(i => (
          <div key={`pts-opp-${i}`} className="oasis-board-points" />
        ))}

        <div className="oasis-board-deck">
          <div className="skeleton-block skeleton-card" />
        </div>
        {columns.map(i => (
          <div key={`oasis-${i}`} className="skeleton-block skeleton-oasis-card">
            <div className="skeleton-oasis-card-circle" />
          </div>
        ))}

        <div key="deck-spacer-my-points" />
        {columns.map(i => (
          <div key={`pts-mine-${i}`} className="oasis-board-points" />
        ))}

        <div key="deck-spacer-my-cards" />
        {columns.map(i => (
          <div key={`mine-${i}`} className="oasis-board-cards oasis-board-cards-mine">
            {renderSlots(cardCounts[i].mine, 'my')}
          </div>
        ))}
      </div>

      {/* The real board shows a move-summary row OR a round-summary row
          (never both at once -- see OasisBoard.js), each with its own
          button. One shared placeholder stands in for whichever will
          actually appear, reserving that space without doubling it up. */}
      <div className="oasis-board-move-summary-row">
        <span className="skeleton-block skeleton-summary-text" />
        <span className="skeleton-block skeleton-button" />
      </div>

      <div className="oasis-board-hand-area">
        <div className="oasis-board-controls-row">
          <div className="oasis-board-controls-left">
            <div className="oasis-board-my-name-row mb-2">
              <span className="skeleton-block skeleton-name" />
              <span className="skeleton-block skeleton-palm-trees" />
            </div>
            <div className="oasis-board-hand">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="skeleton-block skeleton-card" />
              ))}
            </div>
          </div>
          <div className="oasis-board-controls-right">
            <span className="skeleton-block skeleton-button" />
            <span className="skeleton-block skeleton-button" />
          </div>
        </div>
      </div>
    </div>
  );
}
